import axios from 'axios';
import { API_CONFIG } from '../config';
import { getParachuteAuthToken } from '../utils/parachuteAuthToken';
import type { AnalysisWorkflowProgress } from './AnalysisApiClient';
import { normalizeLawFirmUser, type LawFirmUserRow } from './ClientLicensesApiClient';

/** One historical 280G analysis run for a company under a law firm. */
export interface AnalysisRunRow {
  id: string;
  /** Workspace analysis GUID for this run (API field `analysisId`). */
  analysisId?: string;
  /** True when a stored JSON snapshot exists for this run. */
  hasSnapshot?: boolean;
  runDate: string;
  analysisName?: string;
  /** Legacy DB status string (e.g. ok, over_threshold). */
  status: string;
  ranByUserId?: number;
  ranByEmail?: string;
  ranByName?: string;
  individualsCount: number;
  overCount: number;
}

/** One live workspace analysis (Analyses row) for a company. */
export interface CompanyLiveAnalysisRow {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  workflowLastChangedAt?: string | null;
  lastRunAt?: string | null;
  individualsCount: number;
  dealPrice?: number | null;
  afr?: number | null;
  status?: string | null;
  /** Same shape as client GET /analyses list; drives % + label in firm tables. */
  workflowProgress?: AnalysisWorkflowProgress | null;
  /** `editable` | `final` from Analyses.Status (final = workflow locked). */
  analysisPersistedStatus?: string | null;
}

/** Intake + deal context (drives follow-up fields to capture). */
export interface CompanyDealContext {
  dealStructure: string;
  /** Company role in the transaction (target, acquirer, etc.). */
  dealRole: string;
  listingStatus: string;
  notes: string;
}

/**
 * A company record scoped to a law firm (multi-tenant).
 * Each law firm adds companies to run 280G analyses against.
 */
export interface CompanyRecord {
  id: string;
  companyName: string;
  closingDate: string;
  analysesCount: number;
  overThresholdCount: number;
  lastUpdated: string;
  dealStructure?: string;
  dealRole?: string;
  listingStatus?: string;
  /** Overall deal / 280G workflow (intake, in_progress, review, complete, on_hold). */
  workStatus?: string;
  notes?: string;
  analysisRuns?: AnalysisRunRow[];
  /** Live workspace analyses; filled by GET .../companies/:id/analyses. */
  analyses?: CompanyLiveAnalysisRow[];
}

function getAxiosResponseMessage(e: unknown): string | undefined {
  if (!axios.isAxiosError(e) || e.response?.data == null) return undefined;
  const d = e.response.data as { message?: string };
  return typeof d.message === 'string' && d.message.trim() !== '' ? d.message.trim() : undefined;
}

function authHeaders(lawFirmId: string): Record<string, string> {
  const h: Record<string, string> = {
    accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Law-Firm-Id': lawFirmId,
    'X-Firm-Id': lawFirmId,
  };
  const token = getParachuteAuthToken();
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

function companiesResourceUrl(lawFirmId: string, companyId?: string): string {
  return `${API_CONFIG.BASE_URL}${
    companyId
      ? API_CONFIG.ENDPOINTS.lawFirmCompany(lawFirmId, companyId)
      : API_CONFIG.ENDPOINTS.lawFirmCompanies(lawFirmId)
  }`;
}

export async function fetchCompaniesForLawFirm(lawFirmId: string): Promise<CompanyRecord[]> {
  const res = await axios.get<{ data: CompanyRecord[] }>(companiesResourceUrl(lawFirmId), {
    headers: authHeaders(lawFirmId),
  });
  return res.data.data ?? [];
}

export async function fetchCompanyById(lawFirmId: string, id: string): Promise<CompanyRecord | null> {
  try {
    const res = await axios.get<{ data: CompanyRecord }>(companiesResourceUrl(lawFirmId, id), {
      headers: authHeaders(lawFirmId),
    });
    return res.data.data ?? null;
  } catch (e: unknown) {
    const ax = e as { response?: { status?: number } };
    if (ax.response?.status === 404) return null;
    throw e;
  }
}

/** Live 280G analyses for a company (firm + company id); not the full company row. */
export type CompanyAnalysesPayload = {
  analysesCount: number;
  /** Legacy AnalysisRuns (per-company batch history). */
  analysisRuns: AnalysisRunRow[];
  /** Current Analyses workspace rows for this company. */
  analyses: CompanyLiveAnalysisRow[];
};

export async function fetchCompanyAnalyses(lawFirmId: string, companyId: string): Promise<CompanyAnalysesPayload> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.lawFirmCompanyAnalyses(lawFirmId, companyId)}`;
  const res = await axios.get<{ data: CompanyAnalysesPayload }>(url, {
    headers: { ...authHeaders(lawFirmId), 'Cache-Control': 'no-cache' },
    params: { _: Date.now() },
  });
  const d = res.data.data;
  if (!d) throw new Error('Invalid analyses response');
  return {
    analysesCount: d.analysesCount ?? 0,
    analysisRuns: Array.isArray(d.analysisRuns) ? d.analysisRuns : [],
    analyses: Array.isArray(d.analyses) ? d.analyses : [],
  };
}

export type CreateCompanyInput = {
  companyName: string;
  closingDate: string;
  workStatus?: string;
} & CompanyDealContext;

export async function createCompany(lawFirmId: string, input: CreateCompanyInput): Promise<CompanyRecord> {
  try {
    const res = await axios.post<{ data: CompanyRecord }>(
      companiesResourceUrl(lawFirmId),
      {
        companyName: input.companyName,
        closingDate: input.closingDate,
        dealStructure: input.dealStructure,
        dealRole: input.dealRole,
        listingStatus: input.listingStatus,
        ...(input.workStatus != null && String(input.workStatus).trim() !== ''
          ? { workStatus: String(input.workStatus).trim() }
          : {}),
        notes: input.notes ?? '',
      },
      { headers: authHeaders(lawFirmId) }
    );
    return res.data.data;
  } catch (e: unknown) {
    const msg = getAxiosResponseMessage(e);
    if (msg) throw new Error(msg);
    throw e instanceof Error ? e : new Error('Failed to create company');
  }
}

/** PATCH body for `PUT`-style updates; all fields optional on the wire, but send values you want to persist. */
export type PatchCompanyInput = {
  companyName?: string;
  closingDate?: string;
  dealStructure?: string;
  dealRole?: string;
  listingStatus?: string;
  workStatus?: string;
  notes?: string;
};

export async function patchCompany(
  lawFirmId: string,
  companyId: string,
  patch: PatchCompanyInput
): Promise<CompanyRecord> {
  const url = companiesResourceUrl(lawFirmId, companyId);
  try {
    const res = await axios.patch<{ data: CompanyRecord }>(url, patch, { headers: authHeaders(lawFirmId) });
    return res.data.data;
  } catch (e: unknown) {
    const msg = getAxiosResponseMessage(e);
    if (msg) throw new Error(msg);
    throw e instanceof Error ? e : new Error('Failed to update company');
  }
}

/** Client user rows for a company — same shape as `LawFirmUserRow` / API `LawFirmUserResponse`. */
export type CompanyUserRow = LawFirmUserRow;

/** Client accounts linked to this company (firm + company id in path). */
export async function fetchCompanyUsers(lawFirmId: string, companyId: string): Promise<CompanyUserRow[]> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.lawFirmCompanyUsers(lawFirmId, companyId)}`;
  const res = await axios.get<{ data: unknown }>(url, { headers: authHeaders(lawFirmId) });
  const rows = Array.isArray(res.data.data) ? res.data.data : [];
  return rows.map((r) =>
    typeof r === 'object' && r !== null ? normalizeLawFirmUser(r as Record<string, unknown>) : normalizeLawFirmUser({})
  );
}

export type CreateCompanyUserInput = {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
};

export type CompanyUserOnboardingInvite = {
  temporaryPassword: string;
  passwordResetLink: string;
};

export type InviteCompanyUserResult = {
  user: CompanyUserRow;
  invite: CompanyUserOnboardingInvite;
};

/** POST /lawfirms/{lawFirmId}/companies/{companyId}/users — firm admin; creates client login for this company. */
export async function inviteCompanyUser(
  lawFirmId: string,
  companyId: string,
  input: CreateCompanyUserInput
): Promise<InviteCompanyUserResult> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.lawFirmCompanyUsers(lawFirmId, companyId)}`;
  try {
    const res = await axios.post<{ data: unknown }>(
      url,
      {
        email: input.email.trim(),
        firstName: input.firstName.trim(),
        lastName: input.lastName.trim(),
        ...(input.phone != null && String(input.phone).trim() !== '' ? { phone: String(input.phone).trim() } : {}),
      },
      { headers: authHeaders(lawFirmId) }
    );
    const raw = res.data.data;
    if (typeof raw !== 'object' || raw === null) {
      throw new Error('Invalid response from server');
    }
    const o = raw as Record<string, unknown>;
    const userRaw = o.user ?? o.User;
    const invRaw = o.invite ?? o.Invite;
    if (!userRaw || typeof userRaw !== 'object' || !invRaw || typeof invRaw !== 'object') {
      throw new Error('Invalid response from server');
    }
    const inv = invRaw as Record<string, unknown>;
    const tp = inv.temporaryPassword ?? inv.TemporaryPassword;
    const pl = inv.passwordResetLink ?? inv.PasswordResetLink;
    if (typeof tp !== 'string' || typeof pl !== 'string') {
      throw new Error('Invalid response from server');
    }
    return {
      user: normalizeLawFirmUser(userRaw as Record<string, unknown>),
      invite: { temporaryPassword: tp, passwordResetLink: pl },
    };
  } catch (e: unknown) {
    const msg = getAxiosResponseMessage(e);
    if (msg) throw new Error(msg);
    throw e instanceof Error ? e : new Error('Failed to invite company user');
  }
}

/** POST …/companies/{companyId}/users/reset-password — firm admin; body: userId only; returns reset link. */
export async function resetCompanyUserPassword(
  lawFirmId: string,
  companyId: string,
  userId: number
): Promise<{ passwordResetLink: string }> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.lawFirmCompanyUsersResetPassword(lawFirmId, companyId)}`;
  try {
    const res = await axios.post<{ data: unknown }>(url, { userId }, { headers: authHeaders(lawFirmId) });
    const d = res.data.data;
    if (typeof d !== 'object' || d === null) {
      throw new Error('Invalid reset password response');
    }
    const o = d as Record<string, unknown>;
    const link = o.passwordResetLink ?? o.PasswordResetLink;
    if (typeof link !== 'string' || !link) {
      throw new Error('Invalid reset password response');
    }
    return { passwordResetLink: link };
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response) {
      if (e.response.status === 403) {
        throw new Error('You do not have permission to reset passwords.');
      }
      const msg = getAxiosResponseMessage(e);
      if (msg) throw new Error(msg);
      throw new Error(`Failed to reset password (${e.response.status})`);
    }
    throw e instanceof Error ? e : new Error('Failed to reset password');
  }
}

/** PUT …/companies/{companyId}/users/update — firm admin; body: id, firstName, lastName, isActive, optional phone. */
export async function updateCompanyUser(
  lawFirmId: string,
  companyId: string,
  body: {
    id: number;
    firstName: string;
    lastName: string;
    phone?: string;
    isActive: boolean;
  }
): Promise<CompanyUserRow> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.lawFirmCompanyUsersUpdate(lawFirmId, companyId)}`;
  try {
    const res = await axios.put<{ data: unknown }>(
      url,
      {
        id: body.id,
        firstName: body.firstName.trim(),
        lastName: body.lastName.trim(),
        isActive: body.isActive,
        ...(body.phone != null && String(body.phone).trim() !== '' ? { phone: String(body.phone).trim() } : {}),
      },
      { headers: authHeaders(lawFirmId) }
    );
    const raw = res.data.data;
    if (typeof raw === 'object' && raw !== null) {
      return normalizeLawFirmUser(raw as Record<string, unknown>);
    }
    throw new Error('Invalid response from server');
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response) {
      if (e.response.status === 403) {
        throw new Error('You do not have permission to update users.');
      }
      const msg = getAxiosResponseMessage(e);
      if (msg) throw new Error(msg);
      throw new Error(`Failed to update user (${e.response.status})`);
    }
    throw e instanceof Error ? e : new Error('Failed to update company user');
  }
}
